import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import TentativeWspGroups from './TentativeWspGroups';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const EditProductDialog = ({
  editingProduct, setEditingProduct,
  editProductForm, setEditProductForm,
  saveEditProduct,
  enums, brands, vendors,
  handleEditImageUpload, handleBulkImageUpload, handleCameraCapture,
  editUploading,
  editFileInputRef, editCameraInputRef,
  renameSkuColor, removeSkuColor,
}) => {
  const [colorEdits, setColorEdits] = useState({});
  const [vendorSearch, setVendorSearch] = useState('');
  const [vendorDropdownOpen, setVendorDropdownOpen] = useState(false);
  const vendorRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (vendorRef.current && !vendorRef.current.contains(e.target)) setVendorDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!editingProduct) return null;

  const skuColors = [...new Set((editingProduct.skus || []).map(s => s.color).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  const selectedVendor = (vendors || []).find(v => v.id === editProductForm.vendor_id);
  const filteredVendors = (vendors || []).filter(v => {
    if (!vendorSearch.trim()) return true;
    const q = vendorSearch.toLowerCase();
    return v.name?.toLowerCase().includes(q) || v.city?.toLowerCase().includes(q);
  }).slice(0, 15);

  return (
    <Dialog open={!!editingProduct} onOpenChange={() => setEditingProduct(null)}>
      <DialogContent className="max-w-lg z-50 max-h-[90vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Edit {editingProduct.design_code}</DialogTitle>
          <DialogDescription>Update product details including design code. SKU codes will auto-regenerate if design code changes.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label>Design Code *</Label>
            <Input value={editProductForm.design_code} onChange={e => setEditProductForm(p => ({ ...p, design_code: e.target.value }))} placeholder="e.g., 1001" className="uppercase font-bold" data-testid="edit-product-design-code" />
          </div>
          <div className="space-y-2 relative" ref={vendorRef}>
            <Label>Vendor</Label>
            {selectedVendor ? (
              <div className="flex items-center gap-2 h-9 px-3 border rounded-md bg-blue-50 border-blue-200">
                <span className="text-sm font-medium text-blue-800 flex-1 truncate">{selectedVendor.name}{selectedVendor.city ? ` (${selectedVendor.city})` : ''}</span>
                <button type="button" onClick={() => { setEditProductForm(p => ({ ...p, vendor_id: '' })); setVendorSearch(''); }} className="text-blue-400 hover:text-red-500 shrink-0" data-testid="edit-clear-vendor-btn">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            ) : (
              <Input value={vendorSearch} onChange={e => { setVendorSearch(e.target.value); setVendorDropdownOpen(true); }} onFocus={() => setVendorDropdownOpen(true)} placeholder="Search vendor..." data-testid="edit-product-vendor-search" />
            )}
            {vendorDropdownOpen && !selectedVendor && (
              <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid="edit-vendor-dropdown">
                {filteredVendors.length === 0 && <div className="px-3 py-2 text-sm text-gray-400">No vendors found</div>}
                {filteredVendors.map(v => (
                  <div key={v.id} className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm" onMouseDown={(e) => { e.preventDefault(); setEditProductForm(p => ({ ...p, vendor_id: v.id })); setVendorSearch(''); setVendorDropdownOpen(false); }} data-testid={`edit-vendor-option-${v.id}`}>
                    <span className="font-medium">{v.name}</span>
                    {v.city && <span className="text-gray-400 ml-1">({v.city})</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea value={editProductForm.description} onChange={e => setEditProductForm(p => ({ ...p, description: e.target.value }))} placeholder="Product description" data-testid="edit-product-description" />
          </div>
          <div className="space-y-2">
            <Label>Brand</Label>
            <div className="flex flex-wrap gap-2 p-3 border rounded-md bg-gray-50/50 min-h-[42px]" data-testid="edit-product-brand">
              {[...brands].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })).map(b => {
                const isSelected = editProductForm.brand_id === b.id;
                return (
                  <button key={b.id} type="button" onClick={() =>
                    setEditProductForm(p => ({ ...p, brand_id: isSelected ? '' : b.id }))
                  } className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border transition-all ${isSelected ? 'bg-blue-600 text-white border-blue-600 shadow-sm' : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400 hover:bg-blue-50'}`} data-testid={`edit-brand-pill-${b.id}`}>
                    {isSelected && <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>}
                    {b.name}
                  </button>
                );
              })}
              {brands.length === 0 && <span className="text-xs text-gray-400">No brands yet</span>}
            </div>
          </div>
          <div className="space-y-2">
            <Label>Category</Label>
            <div className="flex flex-wrap gap-2 p-3 border rounded-md bg-gray-50/50 min-h-[42px]" data-testid="edit-product-category">
              {enums.categories.map(c => {
                const isSelected = editProductForm.category === c;
                return (
                  <button key={c} type="button" onClick={() =>
                    setEditProductForm(p => ({ ...p, category: isSelected ? '' : c }))
                  } className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border transition-all ${isSelected ? 'bg-blue-600 text-white border-blue-600 shadow-sm' : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400 hover:bg-blue-50'}`} data-testid={`edit-category-pill-${c}`}>
                    {isSelected && <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>}
                    {c}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="space-y-2">
            <Label>Occasion <span className="text-xs text-gray-400 font-normal">(select one or more)</span></Label>
            <div className="flex flex-wrap gap-2 p-3 border rounded-md bg-gray-50/50 min-h-[42px]" data-testid="edit-product-theme">
              {enums.design_themes.map(t => {
                const isSelected = editProductForm.design_theme.includes(t);
                return (
                  <button key={t} type="button" onClick={() => {
                    setEditProductForm(prev => ({
                      ...prev,
                      design_theme: isSelected ? prev.design_theme.filter(x => x !== t) : [...prev.design_theme, t]
                    }));
                  }} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border transition-all ${isSelected ? 'bg-blue-600 text-white border-blue-600 shadow-sm' : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400 hover:bg-blue-50'}`}>
                    {isSelected && <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>}
                    {t}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="space-y-2">
            <Label>Fabric</Label>
            <div className="flex flex-wrap gap-2 p-3 border rounded-md bg-gray-50/50 min-h-[42px]" data-testid="edit-product-fabric">
              {(enums.fabrics || []).map(f => {
                const isSelected = editProductForm.fabric === f;
                return (
                  <button key={f} type="button" onClick={() =>
                    setEditProductForm(p => ({ ...p, fabric: isSelected ? '' : f }))
                  } className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border transition-all ${isSelected ? 'bg-blue-600 text-white border-blue-600 shadow-sm' : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400 hover:bg-blue-50'}`} data-testid={`edit-fabric-pill-${f}`}>
                    {isSelected && <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>}
                    {f}
                  </button>
                );
              })}
            </div>
          </div>
          {/* Image Management in Edit */}
          <div className="space-y-2">
            <Label>Product Images <span className="text-xs text-gray-400 font-normal">(one per colour)</span></Label>
            {editProductForm.images.length > 0 && (
              <div className="flex gap-3 flex-wrap mb-2">
                {editProductForm.images.map((img, idx) => (
                  <div key={idx} className="relative group">
                    <div className="w-16 h-16 rounded-lg overflow-hidden border-2 border-blue-200 shadow-sm">
                      <img src={`${API_BASE}${img}`} alt={`Img ${idx + 1}`} className="w-full h-full object-cover" />
                    </div>
                    <select
                      value={editProductForm.image_colors?.[img] || ''}
                      onChange={e => setEditProductForm(prev => ({ ...prev, image_colors: { ...prev.image_colors, [img]: e.target.value } }))}
                      className="w-16 h-6 mt-1 text-[10px] border border-gray-300 rounded px-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                      data-testid={`edit-image-color-select-${idx}`}
                    >
                      <option value="">Colour</option>
                      {enums.colors.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <button type="button" onClick={() => {
                      setEditProductForm(prev => {
                        const newColors = { ...prev.image_colors };
                        delete newColors[img];
                        return { ...prev, images: prev.images.filter((_, i) => i !== idx), image_colors: newColors };
                      });
                    }} className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`edit-remove-image-${idx}`}>
                      x
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => editFileInputRef.current?.click()} disabled={editUploading} data-testid="edit-add-image-btn">
                {editUploading ? 'Uploading...' : '+ Add Image'}
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => editCameraInputRef.current?.click()} disabled={editUploading} data-testid="edit-camera-btn">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                Camera
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => { const el = document.getElementById('edit-bulk-file-input'); if (el) el.click(); }} disabled={editUploading} data-testid="edit-bulk-upload-btn">
                Bulk Upload
              </Button>
            </div>
            <input ref={editFileInputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={handleEditImageUpload} className="hidden" data-testid="edit-image-file-input" />
            <input ref={editCameraInputRef} type="file" accept="image/*" capture="environment" onChange={e => handleCameraCapture(e, setEditProductForm)} className="hidden" data-testid="edit-camera-file-input" />
            <input id="edit-bulk-file-input" type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={e => handleBulkImageUpload(e.target.files, setEditProductForm, { current: e.target })} className="hidden" data-testid="edit-bulk-file-input" />
          </div>
          {/* Colours in use (drives Sales Orders) — rename fixes spelling across SKUs */}
          {skuColors.length > 0 && (
            <div className="space-y-2">
              <Label>Colours in use <span className="text-xs text-gray-400 font-normal">(updates SKUs &amp; Sales Orders)</span></Label>
              <div className="space-y-1.5 p-3 border rounded-md bg-gray-50/50">
                {skuColors.map(c => {
                  const val = colorEdits[c] ?? c;
                  return (
                    <div key={c} className="flex items-center gap-2" data-testid={`edit-skucolor-row-${c}`}>
                      <Input value={val} onChange={e => setColorEdits(p => ({ ...p, [c]: e.target.value }))} className="h-8 text-sm flex-1" data-testid={`edit-skucolor-input-${c}`} />
                      <Button type="button" size="sm" variant="outline" disabled={!val.trim() || val.trim() === c}
                        onClick={() => renameSkuColor(editingProduct.id, c, val.trim())} className="h-8" data-testid={`edit-skucolor-rename-${c}`}>Rename</Button>
                      <Button type="button" size="sm" variant="ghost" onClick={() => removeSkuColor(editingProduct.id, c)}
                        className="h-8 text-red-500 hover:bg-red-50 px-2" data-testid={`edit-skucolor-remove-${c}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  );
                })}
              </div>
              <p className="text-[11px] text-gray-400">Renaming fixes spelling across stock &amp; orders. Remove is blocked if the colour still has stock.</p>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Tentative WSP <span className="text-xs text-gray-400 font-normal">(optional)</span></Label>
            <Input type="number" min="0" step="0.01" value={editProductForm.tentative_wsp ?? ''}
              onChange={e => setEditProductForm(p => ({ ...p, tentative_wsp: e.target.value }))}
              placeholder="e.g., 750" onWheel={e => e.target.blur()} className="max-w-[200px]" data-testid="edit-product-tentative-wsp" />
            <TentativeWspGroups sizes={enums.sizes || []} groups={editProductForm.tentative_wsp_groups || []}
              onChange={(g) => setEditProductForm(p => ({ ...p, tentative_wsp_groups: g }))} />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => setEditingProduct(null)}>Cancel</Button>
            <Button onClick={saveEditProduct} className="bg-blue-600 hover:bg-blue-700" data-testid="save-edit-product">Save Changes</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditProductDialog;
